import { useCallback, useMemo } from "react";
import { Card, Tooltip, Typography } from "@mui/material";
import { ResponsiveBoxPlot } from "@nivo/boxplot";
import { BoxPlotType, isMaterialBoxPlotType, isPerformanceOverTimeType } from "./types";
import { BoxPlotSummaryTooltip } from "./BoxPlotTooltip";

const BOXPLOT_BLUE = 'rgba(3, 127, 176, 1)';
const BOXPLOT_TRANSPARENT = 'rgba(255, 255, 255, 0)';
const QUANTILES = [ 0.1, 0.25, 0.5, 0.75, 0.9 ];

const darkenColorString = (hex, percent) => {
  // Remove the # in the beginning
  hex = hex.replace(/^#/, '');

  // Convert the 3-character hex code to 6-character if necessary
  if (hex.length === 3) {
    hex = hex.split('').map(h => h + h).join('');
  }

  // Parse the R, G, B values from the hex code
  let r = parseInt(hex.substring(0, 2), 16);
  let g = parseInt(hex.substring(2, 4), 16);
  let b = parseInt(hex.substring(4, 6), 16);

  // Darken each component by the given percentage
  r = Math.floor(r * (1 - percent / 100));
  g = Math.floor(g * (1 - percent / 100));
  b = Math.floor(b * (1 - percent / 100));

  // Return the darkened color as an RGB string
  return `rgb(${r}, ${g}, ${b})`;
}

// Generates a random offset based on a seed (used to keep data point offsets equal between re-renders)
function generateOffset(seed, maxValue) {
  // Constants for random generator (you can tweak these for different results)
  const m = maxValue;     // use max value in dataset as modulus
  const a = 165807;       // multiplier
  let random = seed;

  // Generate a next random number
  random = (random * a) % m;

  // Brings random number between 0 and 1
  const randomBetweenZeroandOne = random / m

  // Alters the random value to be between -0.25 and 0.25 (alter to change range)
  const finalOffset = randomBetweenZeroandOne * 0.5 - 0.25

  return finalOffset;
}

const addCommas = (number: number) => {
  return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

const getDecimalCount = (number: number) => {
  const absNum = Math.abs(number);
  if (absNum < 1) return 3;
  else if (absNum < 10) return 2;
  else if (absNum < 100) return 1;
  else return 0;
}

const formatValue = (value) => `${value.toFixed(getDecimalCount(value))} kgCO₂e/m²`;

const formatAxisValue = (value: number): string => {
  if (value >= 1000000) {
    return (value / 1000000).toFixed(1) + 'M';
  } else if (value >= 1000) {
    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  }
  return value.toString();
};

const findLargestValue = (data, limitToDisplayedProjects = false): number => {
  return data.reduce((max, item) => Math.max(max, !limitToDisplayedProjects || item.displayed ? item.value: 0), 0);
};

const findLargestTopQuantile = (data): number => {
  const valuesByGroup: object = data.reduce((vBG: object, datum: any) => {
    if (datum.group in vBG) vBG[datum.group].push(datum.value || 0);
    else vBG[datum.group] = [ (datum.value || 0) ];
    return vBG;
  }, {});
  const ninetyeths: number[] = Object.values(valuesByGroup).map((values: number[]) => {
    const sortedValues = values.sort((a, b) => a - b);
    const topQuant = QUANTILES[QUANTILES.length - 1];
    return sortedValues[Math.floor(sortedValues.length * topQuant)] || 0;
  })
  return Math.max(...ninetyeths);
};

interface BoxPlotProps {
  data: any[];
  chartType: BoxPlotType;
  legend: string;
  displayOutliers: boolean,
}

const BoxPlot = ({ data, chartType, legend, displayOutliers }: BoxPlotProps) => {

  const shouldShowBoxWhiskers = useCallback((datum: any) => {
    return isPerformanceOverTimeType(chartType) || (datum?.n ?? 0) >= 5;
  }, [chartType]);

  const orderGroups = useCallback((groupA: string, groupB: string) => {
    let comparableA = groupA;
    let comparableB = groupB;

    if (chartType === 'Gross Floor Area') {
      const separatorRegex = /\s*(-+|((T|t)(O|o)))\s*/; // " TO ", "-", " -- ", "to", etc

      // Use the first (lower) number in the range as the comparable value, and strip out the commas
      comparableA = groupA.trim().split(separatorRegex)[0].replaceAll(',', '');
      comparableB = groupB.trim().split(separatorRegex)[0].replaceAll(',', '');
    }

    // Compare the values as numbers if neither are NaN
    const numericalA = parseFloat(comparableA);
    const numericalB = parseFloat(comparableB);
    if (!isNaN(numericalA) && !isNaN(numericalB)) {
      return numericalB - numericalA;
    }
  
    // Otherwise compare them as lowercase strings
    return comparableA.toLowerCase() > comparableB.toLowerCase() ? -1 : 1;
  }, [chartType]);

  const colorByGroup = useMemo(() => {
    return data.reduce((colorByGroupAcc: object, datum: any) => {
      colorByGroupAcc[datum.group] = shouldShowBoxWhiskers(datum) ? BOXPLOT_BLUE : BOXPLOT_TRANSPARENT;
      return colorByGroupAcc;
    }, {});
  }, [data, shouldShowBoxWhiskers]);

  const groups = useMemo(() => {
    const groupsSet = data.reduce((groupsSetAcc: Set<string>, datum: any) => groupsSetAcc.add(datum.group), new Set());
    return Array.from(groupsSet).sort(orderGroups);
  }, [data, orderGroups]);

  const largestGroupLength = useMemo(() => data?.reduce((max, item) => {
    return item.group.length > max ? item.group.length : max;
  }, 0), [data]);

  const largestValue = useMemo(() => findLargestValue(data), data);
  const largestDisplayedValue = useMemo(() => findLargestValue(data, true), data);
  const largestTopQuantile = useMemo(() => findLargestTopQuantile(data), data);

  const offsetConst = Math.log(largestGroupLength) / Math.log(2.1)
  const yAxisOffset = offsetConst * 50
  const yAxisLabelOffset = -offsetConst * 45

  const renderCustomDataPoints = ({ boxPlots, xScale, yScale, width }) => {
    return (
      <g>
        {boxPlots.map((boxPlot) => {
          const { group } = boxPlot.data;
          const points = data.filter((data) => data.group === group && data.displayed);
          return points.map((point, index) => {
            const x = point.value;
            const y = boxPlot.y + boxPlot.height / 2;
            return (
              <Tooltip
                key={`${group}-${index}`}
                title={`${group}: ${addCommas(point.value.toFixed(getDecimalCount(point.value)))} kgCO₂e/m²`}
                arrow
                placement="top"
              >
                <circle
                  cx={xScale(x)}
                  cy={y + generateOffset(point.value, largestValue) * boxPlot.height}
                  r={3}
                  fill={darkenColorString(point.color, 20)}
                  opacity={0.7}
                  style={{ cursor: 'pointer' }}
                />
              </Tooltip>
            );
          });
        })}
      </g>
    );
  };

  return (
    <ResponsiveBoxPlot
      data={data as any}
      margin={{ top: 20, right: 40, bottom: 60, left: yAxisOffset }}
      minValue={0}
      maxValue={displayOutliers && (largestDisplayedValue >= largestTopQuantile) ? largestDisplayedValue * 1.01 : undefined}
      quantiles={QUANTILES}
      groupBy="group"
      groups={groups}
      colors={({ group }) => colorByGroup[group]}
      opacity={0.75}
      inactiveOpacity={0.75}
      activeOpacity={0.75}
      valueFormat={formatValue}
      padding={0.15}
      enableGridX={true}
      enableGridY={false}
      axisRight={null}
      axisBottom={{
        tickSize: 5,
        tickPadding: 5,
        legend: legend,
        legendPosition: 'middle',
        legendOffset: 48,
        format: formatAxisValue
      }}
      axisLeft={{
        tickSize: 5,
        tickPadding: 5,
        tickRotation: 0,
        legend: isMaterialBoxPlotType(chartType) ? 'Primary Building Use Type' : chartType,
        legendPosition: 'middle',
        legendOffset: yAxisLabelOffset,
      }}
      layout="horizontal"
      borderColor={{
        from: 'color',
        modifiers: [
          [
            'darker',
            0.3
          ]
        ]
      }}
      medianColor={{
        from: 'color',
        modifiers: [
          [
            'darker',
            1
          ]
        ]
      }}
      whiskerEndSize={0.6}
      motionConfig="gentle"
      layers={[
        "grid",
        "axes",
        "boxPlots",
        "annotations",
        "markers",
        renderCustomDataPoints
      ]}
      tooltip={(data: any) => shouldShowBoxWhiskers(data?.data) ? <BoxPlotSummaryTooltip {...data} /> : <></>}
    />
  );
};

export default BoxPlot;